import React, { useState } from 'react';
import { Modal, Input, Button, Form, Row, Col, Card, Select, Upload, Radio } from 'antd';
import { AdPreview } from './AdPreview';
import coreAPIService from '../../apiServices/coreAPIService';
import { CTW_API_ENDPOINT } from '../../utils';
import { toast } from '../../common/Popover';
import { uploadMediaWithMeta } from '../../utils/campaignUtils';

interface AddNewAdModalProps {
    visible: boolean;
    onClose: () => void;
    metaAdsetId: string;
    campaignName: string;
    adAccountId: string;
    orgId: string;
    adNum: number;
    onSuccess?: () => void;
}

const AddNewAdModal: React.FC<AddNewAdModalProps> = ({ visible, onClose, metaAdsetId, campaignName, orgId, adNum, onSuccess, adAccountId }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [previewData, setPreviewData] = useState<any>({});
    const [mediaType, setMediaType] = useState<'image' | 'video'>('image');
    const [metaImageHash, setMetaImageHash] = useState('');
    const [metaThumbnailImageHash, setMetaThumbnailImageHash] = useState('');
    const handleFormChange = () => {
        setPreviewData(form.getFieldsValue());
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');


            const payload = {
                adset_id: metaAdsetId,
                ads: [
                    {
                        creative: {
                            name: campaignName + "_adCreative_" + adNum + "_" + dateStr,
                            message: values.message,
                            ...(mediaType === 'image'
                                ? {
                                    picture: values.picture,
                                    image_source: metaImageHash ? 'meta' : 's3',
                                    meta_image_hash: metaImageHash || null
                                }
                                : {
                                    video_id: values.video_id,
                                    thumbnail_image_url: values.thumbnail_image_url,
                                    image_source: metaThumbnailImageHash ? 'meta' : 's3',
                                    meta_thumbnail_image_hash: metaThumbnailImageHash || null
                                }
                            ),
                            link_name: values.link_name,
                            link_description: values.link_description,
                            prefilled_message: values.prefilled_message || '',
                            welcome_message: values.welcome_message || '',
                        },
                        ad: {
                            name: values.adName,
                            status: values.status || 'PAUSED',
                        },
                    },
                ],
            };

            const response = await coreAPIService.post<any>(
                CTW_API_ENDPOINT,
                'v2/campaigns/ctwa/adset/add-ads',
                payload
            );

            setLoading(false);

            if (response.code === 200 || response.meta?.code === 200) {
                toast('success', 'Ad created successfully!');
                form.resetFields();
                setPreviewData({});
                setMetaImageHash('');
                setMetaThumbnailImageHash('');
                onSuccess?.();
                onClose();
            } else {
                toast('error', response.meta?.message || 'Failed to create ad');
            }
        } catch (error: any) {
            setLoading(false);
            if (error.errorFields) {
                toast('warning', 'Please fill in all required fields');
            } else {
                toast('error', 'Error occurred while creating ad');
                console.error('Error creating ad:', error);
            }
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setMetaImageHash('');
        setMetaThumbnailImageHash('');
        onClose();
    };
    const handleUpload = async (file: File) => {
        // Validate file type based on media type
        setLoading(true)
        if (mediaType === 'image' && !file.type.startsWith('image/')) {
            toast('error', 'Please upload an image file');
            return Upload.LIST_IGNORE;
        }

        if (mediaType === 'video' && !file.type.startsWith('video/')) {
            toast('error', 'Please upload a video file');
            return Upload.LIST_IGNORE;
        }

        // Validate aspect ratio (1:1) for images
        if (file.type.startsWith('image/')) {
            const isSquare = await new Promise<boolean>((resolve) => {
                const img = new Image();
                img.src = URL.createObjectURL(file);
                img.onload = () => {
                    const width = img.width;
                    const height = img.height;
                    URL.revokeObjectURL(img.src);
                    // Allow small tolerance for floating point issues
                    // const ratio = width / height;
                    // if (Math.abs(ratio - 1) < 0.01) {
                    //     resolve(true);
                    // } else {
                    //     resolve(false);
                    // }
                    resolve(true);
                };
                img.onerror = () => resolve(false);
            });

            if (!isSquare) {
                toast('error', 'Image must have a 1:1 aspect ratio (square).');
                return Upload.LIST_IGNORE;
            }
        }

        try {
            const uploadedMedia = await uploadMediaWithMeta(file, orgId, adAccountId);
            if (!uploadedMedia.url) throw new Error('Upload failed');

            if (mediaType === 'image') {
                form.setFieldValue('picture', uploadedMedia.url);
                setMetaImageHash(uploadedMedia.hash || '');
                setPreviewData({ ...form.getFieldsValue(), picture: uploadedMedia.url });
            } else {
                form.setFieldValue('video_id', uploadedMedia.url);
                setPreviewData({ ...form.getFieldsValue(), video_id: uploadedMedia.url });
            }
            toast('success', `${mediaType === 'image' ? 'Image' : 'Video'} uploaded successfully`);
            return false;
        } catch (error) {
            console.log(error);
            toast('error', 'Failed to upload media');
            return false;
        } finally {
            setLoading(false)
        }
    };

    const handleThumbnailUpload = async (file: File) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast('error', 'Thumbnail must be an image file');
            return Upload.LIST_IGNORE;
        }

        // Validate aspect ratio (1:1)
        const isSquare = await new Promise<boolean>((resolve) => {
            const img = new Image();
            img.src = URL.createObjectURL(file);
            img.onload = () => {
                const width = img.width;
                const height = img.height;
                URL.revokeObjectURL(img.src);
                // const ratio = width / height;
                // if (Math.abs(ratio - 1) < 0.1) {
                //     resolve(true);
                // } else {
                //     resolve(false);
                // }
                resolve(true);
            };
            img.onerror = () => resolve(false);
        });

        if (!isSquare) {
            toast('error', 'Thumbnail must have a 1:1 aspect ratio (square).');
            return Upload.LIST_IGNORE;
        }

        try {
            const uploadedMedia = await uploadMediaWithMeta(file, orgId, adAccountId);
            // console.log(uploadedMedia.url);

            if (!uploadedMedia.url) throw new Error('Upload failed');
            form.setFieldValue('thumbnail_image_url', uploadedMedia.url);
            setMetaThumbnailImageHash(uploadedMedia.hash || '');
            setPreviewData({ ...form.getFieldsValue(), thumbnail_image_url: uploadedMedia.url, picture: uploadedMedia.url });
            toast('success', 'Thumbnail uploaded successfully');
            return false;
        } catch (error) {
            console.log(error);
            toast('error', 'Failed to upload thumbnail');
            return false;
        }
    };

    return (
        <Modal
            title="Add New Ad"
            className="sm:p-4 p-2"
            open={visible}
            onCancel={handleCancel}
            style={{ top: 20, maxWidth: "95vw" }}
            width={900}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
                    Create Ad
                </Button>,
            ]}
            destroyOnClose
        >
            <Row gutter={32}>

                {/* ================= LEFT : FORM ================= */}
                <Col xs={24} md={24} lg={13} style={{ maxHeight: "calc(100vh - 180px)", overflowY: "auto" }}>
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={{ status: "PAUSED" }}
                        className="space-y-6"
                        onValuesChange={handleFormChange} // Add this
                    >
                        {/* ========= CREATIVE ========= */}
                        <Card title="Creative Details" size="small" className="w-full">

                            <Form.Item
                                label="Ad Name"
                                name="adName"
                                rules={[{ required: true, message: 'Please enter an ad name' }]}
                            >
                                <Input placeholder="Enter ad name" />
                            </Form.Item>

                            <Form.Item
                                label="Primary Text"
                                name="message"
                                rules={[{ required: true }]}
                            >
                                <Input.TextArea rows={4} showCount maxLength={500} />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Headline"
                                        name="link_name"
                                        rules={[{ required: true }]}
                                    >
                                        <Input.TextArea maxLength={40} showCount />
                                    </Form.Item>
                                </Col>

                                <Col span={12}>
                                    <Form.Item
                                        label="Description"
                                        name="link_description"
                                        rules={[{ required: true }]}
                                    >
                                        <Input.TextArea maxLength={200} showCount />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item label="Media Type" required>
                                <Radio.Group
                                    value={mediaType}
                                    onChange={(e) => {
                                        setMediaType(e.target.value);
                                        // Clear media fields when switching type
                                        form.setFieldValue('picture', '');
                                        form.setFieldValue('video_id', '');
                                        form.setFieldValue('thumbnail_image_url', '');
                                        setMetaImageHash('');
                                        setMetaThumbnailImageHash('');
                                        setPreviewData({});
                                    }}
                                >
                                    <Radio value="image">Image</Radio>
                                    <Radio value="video">Video</Radio>
                                </Radio.Group>
                            </Form.Item>

                            {mediaType === 'image' ? (
                                <Form.Item
                                    label="Image"
                                    required
                                    name="picture"
                                    validateStatus={!form.getFieldValue('picture') ? 'error' : ''}
                                    help={!form.getFieldValue('picture') ? 'Please upload or enter image URL' : ''}
                                >
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <Row gutter={16} align="middle">
                                            <Col span={12} className="flex items-center gap-4">
                                                <Upload
                                                    maxCount={1}
                                                    accept="image/jpg,image/jpeg,image/png"
                                                    beforeUpload={handleUpload}
                                                    onRemove={() => {
                                                        form.setFieldValue('picture', '');
                                                        setMetaImageHash('');
                                                        setPreviewData({ ...form.getFieldsValue(), picture: '' });
                                                    }}
                                                    showUploadList={false}
                                                >
                                                    <Button>
                                                        Upload Image
                                                    </Button>
                                                </Upload>
                                                <span className="text-gray-400">or</span>
                                            </Col>
                                            <Col span={12}>
                                                <Input
                                                    placeholder="Enter image URL"
                                                    value={form.getFieldValue('picture')}
                                                    onChange={(e) => {
                                                        form.setFieldValue('picture', e.target.value);
                                                        setMetaImageHash('');
                                                        setPreviewData({ ...form.getFieldsValue(), picture: e.target.value });
                                                    }}
                                                    className="rounded-md"
                                                />
                                            </Col>
                                        </Row>
                                        {form.getFieldValue('picture') && (
                                            <div className="mt-4">
                                                <img
                                                    src={form.getFieldValue('picture')}
                                                    alt="Ad Media"
                                                    className="w-32 h-32 object-cover rounded-md border border-gray-300"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </Form.Item>
                            ) : (
                                <>
                                    <Form.Item
                                        label="Video"
                                        required
                                        name="video_id"
                                        validateStatus={!form.getFieldValue('video_id') ? 'error' : ''}
                                        help={!form.getFieldValue('video_id') ? 'Please upload a video' : ''}
                                    >
                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            <Upload
                                                maxCount={1}
                                                accept="video/mp4,video/quicktime"
                                                beforeUpload={handleUpload}
                                                onRemove={() => {
                                                    form.setFieldValue('video_id', '');
                                                    setPreviewData({ ...form.getFieldsValue(), video_id: '' });
                                                }}
                                                showUploadList={false}
                                            >
                                                <Button>
                                                    Upload Video
                                                </Button>
                                            </Upload>
                                            {form.getFieldValue('video_id') && (
                                                <div className="mt-2 text-sm text-green-600">
                                                    ✓ Video uploaded successfully
                                                </div>
                                            )}
                                        </div>
                                    </Form.Item>

                                    <Form.Item
                                        label="Thumbnail"
                                        required
                                        name="thumbnail_image_url"
                                        validateStatus={!form.getFieldValue('thumbnail_image_url') ? 'error' : ''}
                                        help={!form.getFieldValue('thumbnail_image_url') ? 'Please upload or enter thumbnail URL' : ''}
                                    >
                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            <Row gutter={16} align="middle">
                                                <Col span={12} className="flex items-center gap-4">
                                                    <Upload
                                                        maxCount={1}
                                                        accept="image/jpg,image/jpeg,image/png"
                                                        beforeUpload={handleThumbnailUpload}
                                                        onRemove={() => {
                                                            form.setFieldValue('thumbnail_image_url', '');
                                                            setMetaThumbnailImageHash('');
                                                            setPreviewData({ ...form.getFieldsValue(), thumbnail_image_url: '', picture: '' });
                                                        }}
                                                        showUploadList={false}
                                                    >
                                                        <Button>
                                                            Upload Thumbnail
                                                        </Button>
                                                    </Upload>
                                                    <span className="text-gray-400">or</span>
                                                </Col>
                                                <Col span={12}>
                                                    <Input
                                                        placeholder="Enter thumbnail URL"
                                                        value={form.getFieldValue('thumbnail_image_url')}
                                                        onChange={(e) => {
                                                            form.setFieldValue('thumbnail_image_url', e.target.value);
                                                            setMetaThumbnailImageHash('');
                                                            setPreviewData({ ...form.getFieldsValue(), thumbnail_image_url: e.target.value, picture: e.target.value });
                                                        }}
                                                        className="rounded-md"
                                                    />
                                                </Col>
                                            </Row>
                                            {form.getFieldValue('thumbnail_image_url') && (
                                                <div className="mt-4">
                                                    <img
                                                        src={form.getFieldValue('thumbnail_image_url')}
                                                        alt="Video Thumbnail"
                                                        className="w-32 h-32 object-cover rounded-md border border-gray-300"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </Form.Item>
                                </>
                            )}
                        </Card>

                        {/* ========= WHATSAPP ========= */}
                        <Card title="WhatsApp Messages (Optional)" size="small" className="w-full">
                            <Form.Item
                                label="Prefilled Message"
                                name="prefilled_message"
                            >
                                <Input.TextArea rows={2} />
                            </Form.Item>

                            <Form.Item
                                label="Welcome Message"
                                name="welcome_message"
                            >
                                <Input.TextArea rows={2} />
                            </Form.Item>
                        </Card>

                    </Form>
                </Col>

                {/* ================= RIGHT : PREVIEW ================= */}
                <Col xs={24} md={24} lg={11}>
                    <div className="lg:sticky lg:top-6">
                        <AdPreview
                            primaryText={previewData.message}
                            headline={previewData.link_name}
                            descriptionText={previewData.link_description}
                            mediaUrl={previewData?.picture?.length ? previewData.picture : previewData.video_thumbnail_url}
                            facebookPage={previewData.facebook_page}
                        />
                    </div>
                </Col>

            </Row>

        </Modal>
    );
};

export default AddNewAdModal;
